import * as z from "zod";
import { ALL_RADIOS, ALL_STATIONS } from "./audio/radioConfig";
import { ALL_PLAYLISTS } from "./audio/playlistConfig";
import { ALL_SCENES } from "./scenes";
import { ENTITIES, STATE_TYPE, STATES } from "./states/entities";

const PositionNumber = z.number().max(100).min(0);
export const PositionSchema = z.object({
	x: PositionNumber,
	y: PositionNumber,
	height: PositionNumber,
	width: PositionNumber,
	rotation: z.number().optional(),
	zIndex: z.number().optional(),
});

export const ScenesSchema = z.enum(ALL_SCENES);

export const NavigationDataSchema = z.object({
	id: z.string(),
	up: z.string().nullable(),
	down: z.string().nullable(),
	left: z.string().nullable(),
	right: z.string().nullable(),
	initialFocus: z.boolean(),
});

export const NavigationArrowsSchema = z.object({
	right: z.boolean(),
	left: z.boolean(),
	up: z.boolean(),
	down: z.boolean(),
});

export const RadioNameSchema = z.enum(ALL_RADIOS);
export const AllPlaylistsSchema = z.enum(ALL_PLAYLISTS);
export const GainSchema = z.number().min(0).max(1);

export const VerbPlaySoundDataSchema = z.object({
	playlist: AllPlaylistsSchema,
	gain: GainSchema,
});

export const VerbSetVolumeDataSchema = z.object({
	radio: RadioNameSchema,
	direction: z.enum(["up", "down"]),
});

export const VerbChangeGlobalEntityStateTypeSchema = z.enum(["set", "toggle", "random"]);

export const VerbChangeGlobalEntityStateDataSchema = z.object({
	stateKey: z.string(),
	type: VerbChangeGlobalEntityStateTypeSchema,
	options: z.array(z.string()),
});

export const VerbNavigateDataSchema = z.object({
	url: z.string(),
	delay: z.number().min(0).optional(),
});

export const VerbSchema = z.discriminatedUnion("name", [
	z.object({
		name: z.literal("NAVIGATE"),
		data: VerbNavigateDataSchema,
	}),
	z.object({
		name: z.literal("BACK"),
		data: z.undefined().optional(),
	}),
	z.object({
		name: z.literal("TOGGLE_RADIO"),
		data: RadioNameSchema,
	}),
	z.object({
		name: z.literal("CHANGE_STATION"),
		data: RadioNameSchema,
	}),
	z.object({
		name: z.literal("PLAY_SOUND"),
		data: VerbPlaySoundDataSchema,
	}),
	z.object({
		name: z.literal("SET_VOLUME"),
		data: VerbSetVolumeDataSchema,
	}),
	z.object({
		name: z.literal("CHANGE_SCENE_STATE"),
		data: VerbChangeGlobalEntityStateDataSchema,
	}),
]);

export const SceneComponentConfigSchema = z.object({
	id: z.string(),
	src: z.string(),
	protected: z.boolean(),
	position: z.object({
		hitbox: PositionSchema,
		icon: PositionSchema,
	}),
	verbs: z.array(VerbSchema),
	hoveredSrc: z.optional(z.string()),
	disabled: z.boolean().optional(),
});

// TODO: TEMP FIX,
//  - handle USER_PROFILE seperately
//  - remove .omit(id: true)
export const SceneComponentSchema = SceneComponentConfigSchema.extend({
	navigation: NavigationDataSchema,
	isUserProfile: z.optional(z.boolean()),
}).omit({ id: true });

export const UserProfileMenuConfigSchema = z.discriminatedUnion("isUserProfile", [
	z.object({
		isUserProfile: z.literal(false),
	}),
	z.object({
		isUserProfile: z.literal(true),
		navigation: NavigationDataSchema,
	}),
]);

export const NavMenuConfigSchema = z.object({
	disable: z.boolean(),
});

export const EdgeSchema = z.object({
	from: z.string(),
	to: z.string(),
	direction: z.enum(["up", "down", "left", "right"]),
});

export const SceneInteractivesConfigUISchema = z
	.object({
		NAV_MENU: NavMenuConfigSchema.optional(),
		USER_PROFILE: z
			.object({
				isVisible: z.boolean().optional(),
			})
			.optional(),
		INITIAL_FOCUS: z.string(),
	})
	.optional();

export const SceneInteractivesConfigSchema = z.object({
	UI: SceneInteractivesConfigUISchema,
	INTERACTIVES: z.record(z.string(), SceneComponentConfigSchema).optional(),
	NAV_GRAPH: z.array(EdgeSchema).optional(),
});

export const SceneInteractivesSchema = z.object({
	UI: z.object({
		NAV_MENU: NavMenuConfigSchema.optional(),
		USER_PROFILE: UserProfileMenuConfigSchema.optional(),
		INITIAL_FOCUS: z.string(),
	}),
	INTERACTIVES: z.record(z.string(), SceneComponentSchema).optional(),
	NAV_GRAPH: z.array(EdgeSchema).optional(),
});

export const SceneBackgroundDataSchema = z.object({
	src: z.string(),
	videoName: z.string(),
});

export const RadioSchema = z.object({
	name: RadioNameSchema,
	gain: GainSchema,
});

export const AudioTrackSchema = z.object({
	source: z.string(),
	defaultGain: GainSchema,
});

export const PlaylistSchema = z.object({
	gain: GainSchema,
	playlist: z.array(AudioTrackSchema),
	isLooping: z.boolean(),
	name: z.string(),
});

export const SceneAudioConfigSchema = z.object({
	radios: z.array(RadioSchema),
	playlists: z.array(PlaylistSchema),
});

export const DecodedTrackSchema = AudioTrackSchema.extend({
	data: z.instanceof(AudioBuffer),
});

export const ActiveTrackSchema = DecodedTrackSchema.extend({
	sourceNode: z.instanceof(AudioBufferSourceNode),
	gainNode: z.instanceof(GainNode),
});

export const ActivePlaylistSchema = PlaylistSchema.extend({
	tracks: z.array(ActiveTrackSchema),
});

export const RadiosConfigSchema = z.object({
	name: z.enum(ALL_RADIOS),
	gain: GainSchema,
});

export const VolumeScaleTypeSchema = z.enum(["linear", "logarithmic"]);

export const RadioStationNameSchema = z.enum(ALL_STATIONS);

export const RadioConfigSchema = z.object({
	numberOfSteps: z.number(),
	maxGain: z.number(),
	scaleType: VolumeScaleTypeSchema,
});

export const RadioStateSchema = z.object({
	isOn: z.boolean(),
	station: RadioStationNameSchema,
	gain: GainSchema,
	volumeLevel: z.number().min(0),
	config: RadioConfigSchema,
});

export const SceneDataSchema = z.object({
	path: z.string(),
	background: SceneBackgroundDataSchema,
	audio: SceneAudioConfigSchema,
	navigateAfterVideo: z.string().optional(),
	interactiveComponents: SceneInteractivesSchema,
	disabled: z.boolean().optional(),
});

export const FireplaceStateSchema = z.object({
	type: z.literal(STATE_TYPE.FIREPLACE),
	id: z.enum([ENTITIES.FIREPLACE.CABIN]),
	value: z.enum([STATES.FIREPLACE.ON, STATES.FIREPLACE.OFF]),
});

export const WeatherStateSchema = z.object({
	type: z.literal(STATE_TYPE.WEATHER),
	id: z.enum([ENTITIES.WEATHER.CABIN]),
	value: z.enum([STATES.WEATHER.DRIZZLE]),
});

export const PetStateSchema = z.object({
	type: z.literal(STATE_TYPE.CABIN_CAT),
	id: z.enum([ENTITIES.CABIN_CAT.CABIN]),
	value: z.enum([STATES.CABIN_CAT.TWITCH, STATES.CABIN_CAT.YAWN]),
});

export const EntitySchema = z.discriminatedUnion("type", [
	FireplaceStateSchema,
	WeatherStateSchema,
	PetStateSchema,
]);

export const SceneStateSchema = z.object({
	audio: z.array(EntitySchema).readonly(),
	video: z.array(EntitySchema).readonly(),
});

export const SceneDataConfigSchema = z.object({
	path: z.string(),
	background: SceneBackgroundDataSchema,
	audio: SceneAudioConfigSchema,
	state: SceneStateSchema,
	navigateAfterVideo: z.string().optional(),
	interactiveComponents: SceneInteractivesConfigSchema,
	disabled: z.boolean().optional(),
});
